import { api } from '../lib/api';
import type { DroneAccessToken, DroneCredentialMode } from '@shared/types';

/**
 * 无人机访问令牌缓存。
 *
 * 令牌由平台 Worker 签发（`POST /api/drones/:id/token`），有效期很短。
 * 这里在到期前 15 秒提前刷新，并合并并发请求，避免 WS 重连风暴时打爆铸造接口。
 */
const REFRESH_MARGIN_MS = 15_000;

const cache = new Map<string, DroneAccessToken>();
const inflight = new Map<string, Promise<DroneAccessToken>>();

export async function acquireDroneToken(
  droneId: string,
  mode: DroneCredentialMode,
): Promise<DroneAccessToken> {
  if (mode === 'none') {
    return { token: '', expiresAt: Number.MAX_SAFE_INTEGER, droneId, mode };
  }

  const cached = cache.get(droneId);
  if (cached && cached.expiresAt - REFRESH_MARGIN_MS > Date.now()) return cached;

  const existing = inflight.get(droneId);
  if (existing) return existing;

  const request = api
    .post<DroneAccessToken>(`/api/drones/${droneId}/token`)
    .then((token) => {
      cache.set(droneId, token);
      return token;
    })
    .finally(() => {
      inflight.delete(droneId);
    });

  inflight.set(droneId, request);
  return request;
}

export function invalidateDroneToken(droneId: string): void {
  cache.delete(droneId);
}

export function clearDroneTokens(): void {
  cache.clear();
  inflight.clear();
}

/** 供无人机侧请求复用的鉴权头。 */
export function authHeaders(token: DroneAccessToken): Record<string, string> {
  if (!token.token || token.mode === 'none') return {};
  if (token.mode === 'apiKey') {
    return { Authorization: `Bearer ${token.token}`, 'X-Api-Key': token.token };
  }
  return { Authorization: `Bearer ${token.token}` };
}
